'use client'

import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import PartnerSwitcher from './PartnerSwitcher'

/**
 * Compact partner logo + name, shown at the right end of the partner portal's
 * tab row (both the in-page tab bar on the app host and the PortalShell header
 * on the standalone portal host). Self-contained: fetches the caller's partner
 * and renders nothing until it's available / if the caller isn't a partner.
 *
 * For a 42a operator the static name/logo is replaced by PartnerSwitcher — the
 * same slot, but a searchable picker that switches partners in place. The gate
 * is "caller is an operator_42a", NOT "a session is open": an operator who is
 * also a partner user of their own arrives here with no session and still needs
 * to reach other partners. getPartnerSwitcherContext returns null for everyone
 * else, so partner users always get the plain badge below.
 */
export default function PartnerBrandBadge({ className }: { className?: string }) {
  const partner = useQuery(api.partners.getMyPartner)
  const switcherContext = useQuery(api.admin.partnerImpersonation.getPartnerSwitcherContext)

  if (switcherContext) {
    // `partner` is the EFFECTIVE partner (impersonated during a session, the
    // operator's own otherwise) — exactly what the trigger should display.
    return (
      <PartnerSwitcher
        className={className}
        currentPartnerId={partner?._id}
        currentName={partner?.name}
        logoUrl={partner?.logoUrl ?? null}
      />
    )
  }

  if (!partner) return null

  return (
    <div className={`flex items-center min-w-0 ${className ?? ''}`}>
      {partner.logoUrl ? (
        // Logo present → show it alone.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={partner.logoUrl}
          alt={partner.name}
          className="h-6 w-auto max-w-[140px] object-contain"
        />
      ) : (
        // No logo → fall back to the name.
        <span className="text-sm font-medium truncate" style={{ color: 'var(--color-text-secondary)' }}>
          {partner.name}
        </span>
      )}
    </div>
  )
}
